import React from 'react';

import { CalendarDateService, dateAdapter, DateObject } from '@uplift/services';
import { Box, Collapse, Divider } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { MobileCalendarComponents } from '../shared';
import { DateFormats } from '@uplift/constants';
import { MobileCalendarWeekdays } from './MobileCalendarWeekdays.component';
import { InfinityScroll, InfinityScrollItem } from './InfiniteScrollCards';
import { MobileCalendarWeeks } from './MobileCalendarWeeks.component';


const MobileCalendarScrollableBody: React.VFC<{
  today: DateObject;
  date: DateObject;
  currentMonth: DateObject;
  setCurrentMonth: (d: string) => void;
  components?: MobileCalendarComponents;
  handleDateSelect: (d: DateObject) => void;
  disablePastDays?: boolean;
}> = ({
  today,
  date,
  currentMonth,
  setCurrentMonth,
  components,
  handleDateSelect,
  disablePastDays,
}) => {
  const prevMonth = React.useMemo(() => dateAdapter.addMonths(currentMonth, -1), [ currentMonth ]);
  const nextMonth = React.useMemo(() => dateAdapter.addMonths(currentMonth, 1), [ currentMonth ]);

  const WeeksComponent = components?.Weeks || MobileCalendarWeeks;

  return (
    <InfinityScroll
      currentSlide={currentMonth.format(DateFormats.ISO_DATE)}
      onScrollEnd={direction => {
        if (direction === 'prev') {
          setCurrentMonth(prevMonth.format(DateFormats.ISO_DATE));
        } else if (direction === 'next') {
          setCurrentMonth(nextMonth.format(DateFormats.ISO_DATE));
        }
      }}
    >
      <InfinityScrollItem>
        <WeeksComponent
          date={date}
          today={today}
          currentMonth={prevMonth}
          components={components}
          handleDateSelect={handleDateSelect}
          disablePastDays={disablePastDays}
        />
      </InfinityScrollItem>
      <InfinityScrollItem>
        <WeeksComponent
          date={date}
          today={today}
          currentMonth={currentMonth}
          components={components}
          handleDateSelect={handleDateSelect}
          disablePastDays={disablePastDays}
        />
      </InfinityScrollItem>
      <InfinityScrollItem>
        <WeeksComponent
          date={date}
          today={today}
          currentMonth={nextMonth}
          components={components}
          handleDateSelect={handleDateSelect}
          disablePastDays={disablePastDays}
        />
      </InfinityScrollItem>
    </InfinityScroll>
  );
};


const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    paddingBottom: theme.spacing(0.5),
  },
  weekdays: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    display: 'flex',
    width: '100%',
    userSelect: 'none',
  },
  week: {
    display: 'flex',
  }
}));


export type MobileCalendarBodyProps = {
  today: string;
  date: string;
  setDate: (d: string) => void;
  currentMonth: string;
  setCurrentMonth: (d: string) => void;
  expanded?: boolean;
  components?: MobileCalendarComponents;
  scrollable?: boolean;
  disablePastDays?: boolean;
};


export const MobileCalendarBody: React.FC<MobileCalendarBodyProps> = ({
  setDate,
  setCurrentMonth,
  components,
  expanded = true,
  scrollable = true,
  ...props
}) => {
  const { classes } = useStyles();

  const today = React.useMemo(() => CalendarDateService.date(props.today, DateFormats.ISO_DATE), [ props.today ]);
  const date = React.useMemo(() => CalendarDateService.date(props.date, DateFormats.ISO_DATE), [ props.date ]);
  const currentMonth = React.useMemo(() => CalendarDateService.date(props.currentMonth, DateFormats.ISO_DATE), [ props.currentMonth ]);

  const handleDateSelect = React.useCallback((d: DateObject) => {
    setCurrentMonth(d.format(DateFormats.ISO_DATE));
    setDate(d.format(DateFormats.ISO_DATE));
  }, [ setDate, setCurrentMonth ]);

  const WeeksComponent = components?.Weeks || MobileCalendarWeeks;

  return (
    <Collapse in={expanded} collapsedSize={12}>
      <Box className={classes.root}>
        <MobileCalendarWeekdays />
        <Divider />
        {scrollable ? (
          <MobileCalendarScrollableBody
            today={today}
            date={date}
            currentMonth={currentMonth}
            setCurrentMonth={setCurrentMonth}
            components={components}
            handleDateSelect={handleDateSelect}
            disablePastDays={props.disablePastDays}
          />
        ) : (
          <WeeksComponent
            date={date}
            today={today}
            currentMonth={currentMonth}
            components={components}
            handleDateSelect={handleDateSelect}
            disablePastDays={props.disablePastDays}
          />
        )}
      </Box>
    </Collapse>
  );
};
